'use client';

import { CheckIcon, CopyIcon } from 'lucide-react';
import type { ComponentProps, CSSProperties, HTMLAttributes } from 'react';
import { createContext, memo, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BundledLanguage, BundledTheme, HighlighterGeneric, ThemedToken } from 'shiki';
import { createHighlighter } from 'shiki';
import { createJavaScriptRegexEngine } from 'shiki/engine/javascript';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

// Shiki uses bitflags for font styles: 1=italic, 2=bold, 4=underline
// biome-ignore lint/suspicious/noBitwiseOperators: shiki bitflag check

const isItalic = (fontStyle: number | undefined) => fontStyle && fontStyle & 1;
// biome-ignore lint/suspicious/noBitwiseOperators: shiki bitflag check

const isBold = (fontStyle: number | undefined) => fontStyle && fontStyle & 2;
const isUnderline = (fontStyle: number | undefined) =>
  // biome-ignore lint/suspicious/noBitwiseOperators: shiki bitflag check
  fontStyle && fontStyle & 4;

// Transform tokens to include pre-computed keys to avoid noArrayIndexKey lint
type KeyedToken = {
  token: ThemedToken;
  key: string;
};
type KeyedLine = {
  tokens: KeyedToken[];
  key: string;
};

type CodeBlockLanguage = BundledLanguage | 'text';

const addKeysToTokens = (lines: ThemedToken[][]): KeyedLine[] =>
  lines.map((line, lineIdx) => ({
    key: `line-${lineIdx}`,
    tokens: line.map((token, tokenIdx) => ({
      key: `line-${lineIdx}-${tokenIdx}`,
      token,
    })),
  }));

// Line number styles using CSS counters
const LINE_NUMBER_CLASSES = cn(
  'block',
  'before:content-[counter(line)]',
  'before:inline-block',
  'before:[counter-increment:line]',
  'before:w-8',
  'before:mr-4',
  'before:text-right',
  'before:text-muted-foreground/50',
  'before:font-mono',
  'before:select-none',
);

const TOKEN_THEME_CLASSES = cn(
  'text-[var(--shiki-light)]',
  'dark:text-[var(--shiki-dark)]',
  'bg-[var(--shiki-light-bg)]',
  'dark:bg-[var(--shiki-dark-bg)]',
  '[font-weight:var(--shiki-light-font-weight)]',
  'dark:[font-weight:var(--shiki-dark-font-weight)]',
  '[font-style:var(--shiki-light-font-style)]',
  'dark:[font-style:var(--shiki-dark-font-style)]',
  '[text-decoration:var(--shiki-light-text-decoration)]',
  'dark:[text-decoration:var(--shiki-dark-text-decoration)]',
);

const getThemedStyleValue = (
  styles: Record<string, string> | undefined,
  property: string,
): string | undefined => {
  const value = styles?.[property];
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const getFirstThemedStyleValue = (
  styles: Record<string, string> | undefined,
  properties: string[],
): string | undefined => {
  for (const property of properties) {
    const value = getThemedStyleValue(styles, property);
    if (value) {
      return value;
    }
  }

  return undefined;
};

const buildTokenThemeStyle = (token: ThemedToken): CSSProperties => {
  const htmlStyle = token.htmlStyle as Record<string, string> | undefined;
  const fallbackWeight = isBold(token.fontStyle) ? 'bold' : 'inherit';
  const fallbackFontStyle = isItalic(token.fontStyle) ? 'italic' : 'inherit';
  const fallbackTextDecoration = isUnderline(token.fontStyle) ? 'underline' : 'none';
  const lightColor = getFirstThemedStyleValue(htmlStyle, ['color']) ?? token.color ?? 'inherit';
  const lightBackground =
    getFirstThemedStyleValue(htmlStyle, [
      '--shiki-light-bg',
      'backgroundColor',
      'background-color',
    ]) ??
    token.bgColor ??
    'transparent';

  return {
    '--shiki-light': lightColor,
    '--shiki-dark': getFirstThemedStyleValue(htmlStyle, ['--shiki-dark']) ?? lightColor,
    '--shiki-light-bg': lightBackground,
    '--shiki-dark-bg': getFirstThemedStyleValue(htmlStyle, ['--shiki-dark-bg']) ?? lightBackground,
    '--shiki-light-font-weight':
      getFirstThemedStyleValue(htmlStyle, [
        '--shiki-light-font-weight',
        'fontWeight',
        'font-weight',
      ]) ?? fallbackWeight,
    '--shiki-dark-font-weight':
      getFirstThemedStyleValue(htmlStyle, [
        '--shiki-dark-font-weight',
        '--shiki-light-font-weight',
        'fontWeight',
        'font-weight',
      ]) ?? fallbackWeight,
    '--shiki-light-font-style':
      getFirstThemedStyleValue(htmlStyle, [
        '--shiki-light-font-style',
        'fontStyle',
        'font-style',
      ]) ?? fallbackFontStyle,
    '--shiki-dark-font-style':
      getFirstThemedStyleValue(htmlStyle, [
        '--shiki-dark-font-style',
        '--shiki-light-font-style',
        'fontStyle',
        'font-style',
      ]) ?? fallbackFontStyle,
    '--shiki-light-text-decoration':
      getFirstThemedStyleValue(htmlStyle, [
        '--shiki-light-text-decoration',
        'textDecoration',
        'text-decoration',
      ]) ?? fallbackTextDecoration,
    '--shiki-dark-text-decoration':
      getFirstThemedStyleValue(htmlStyle, [
        '--shiki-dark-text-decoration',
        '--shiki-light-text-decoration',
        'textDecoration',
        'text-decoration',
      ]) ?? fallbackTextDecoration,
  } as CSSProperties;
};

// Token rendering component
const TokenSpan = ({ token }: { token: ThemedToken }) => (
  <span
    className={TOKEN_THEME_CLASSES}
    // Shiki stores token-level dark theme values in CSS variables. Mirror them onto
    // actual dark-mode styles so highlighted tokens stay legible on dark code blocks.
    style={buildTokenThemeStyle(token)}
  >
    {token.content}
  </span>
);

type HighlightTone = 'info' | 'success';

const HIGHLIGHT_CLASSES = {
  info: '-mx-4 px-4 bg-orange-500/15 shadow-[inset_3px_0_0_var(--color-orange-400)]',
  success: '-mx-4 px-4 bg-green-500/20 shadow-[inset_3px_0_0_#22c55e]',
};

// Line rendering component
const LineSpan = ({
  keyedLine,
  showLineNumbers,
  isHighlighted,
  isHighlightStart,
  isHighlightEnd,
  highlightTone,
}: {
  keyedLine: KeyedLine;
  showLineNumbers: boolean;
  isHighlighted: boolean;
  isHighlightStart: boolean;
  isHighlightEnd: boolean;
  highlightTone: HighlightTone;
}) => (
  <span
    className={cn(
      showLineNumbers ? LINE_NUMBER_CLASSES : 'block',
      isHighlighted && HIGHLIGHT_CLASSES[highlightTone],
      isHighlighted && highlightTone === 'info' && isHighlightStart && 'pt-1',
      isHighlighted && highlightTone === 'info' && isHighlightEnd && 'pb-1',
    )}
    data-highlighted={isHighlighted || undefined}
  >
    {keyedLine.tokens.length === 0
      ? '\n'
      : keyedLine.tokens.map(({ token, key }) => <TokenSpan key={key} token={token} />)}
  </span>
);

// Types
type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string;
  language: CodeBlockLanguage;
  showLineNumbers?: boolean;
  highlightedLines?: number[];
  highlightTone?: HighlightTone;
};

type TokenizedCode = {
  tokens: ThemedToken[][];
  fg: string;
  bg: string;
};

type CodeBlockContextType = {
  code: string;
};

// Context
const CodeBlockContext = createContext<CodeBlockContextType>({
  code: '',
});

// Highlighter cache (singleton per language)
const highlighterCache = new Map<
  string,
  Promise<HighlighterGeneric<BundledLanguage, BundledTheme>>
>();

// Token cache
const tokensCache = new Map<string, TokenizedCode>();

// Subscribers for async token updates
const subscribers = new Map<string, Set<(result: TokenizedCode) => void>>();

const getTokensCacheKey = (code: string, language: CodeBlockLanguage) => {
  return JSON.stringify([language, code]);
};

const getHighlighter = (
  language: BundledLanguage,
): Promise<HighlighterGeneric<BundledLanguage, BundledTheme>> => {
  const cached = highlighterCache.get(language);
  if (cached) {
    return cached;
  }

  const highlighterPromise = createHighlighter({
    langs: [language],
    themes: ['github-light', 'github-dark'],
    engine: createJavaScriptRegexEngine(),
  });

  highlighterCache.set(language, highlighterPromise);
  return highlighterPromise;
};

// Create raw tokens for immediate display while highlighting loads
const createRawTokens = (code: string): TokenizedCode => ({
  bg: 'transparent',
  fg: 'inherit',
  tokens: code.split('\n').map((line) =>
    line === ''
      ? []
      : [
          {
            color: 'inherit',
            content: line,
          } as ThemedToken,
        ],
  ),
});

// Synchronous highlight with callback for async results
export const highlightCode = (
  code: string,
  language: CodeBlockLanguage,
  callback?: (result: TokenizedCode) => void,
): TokenizedCode | null => {
  if (language === 'text') {
    return createRawTokens(code);
  }

  const tokensCacheKey = getTokensCacheKey(code, language);

  // Return cached result if available
  const cached = tokensCache.get(tokensCacheKey);
  if (cached) {
    return cached;
  }

  // Subscribe callback if provided
  if (callback) {
    if (!subscribers.has(tokensCacheKey)) {
      subscribers.set(tokensCacheKey, new Set());
    }
    subscribers.get(tokensCacheKey)?.add(callback);
  }

  // Start highlighting in background - fire-and-forget async pattern
  getHighlighter(language)
    .then((highlighter) => {
      const availableLangs = highlighter.getLoadedLanguages();
      const langToUse = availableLangs.includes(language) ? language : 'md';

      const result = highlighter.codeToTokens(code, {
        lang: langToUse,
        themes: {
          dark: 'github-dark',
          light: 'github-light',
        },
      });

      const tokenized: TokenizedCode = {
        bg: result.bg ?? 'transparent',
        fg: result.fg ?? 'inherit',
        tokens: result.tokens,
      };

      // Cache the result
      tokensCache.set(tokensCacheKey, tokenized);

      // Notify all subscribers
      const subs = subscribers.get(tokensCacheKey);
      if (subs) {
        for (const sub of subs) {
          sub(tokenized);
        }
        subscribers.delete(tokensCacheKey);
      }
    })
    .catch((error) => {
      console.error('Failed to highlight code:', error);
      subscribers.delete(tokensCacheKey);
    });

  return null;
};

const CodeBlockBody = memo(
  ({
    tokenized,
    showLineNumbers,
    highlightedLines,
    highlightTone = 'info',
    className,
  }: {
    tokenized: TokenizedCode;
    showLineNumbers: boolean;
    highlightedLines?: number[];
    highlightTone?: HighlightTone;
    className?: string;
  }) => {
    // Shiki dual-theme codeToTokens returns composite fg/bg strings like
    // "#24292e;--shiki-dark:#e1e4e8" for fg and "#fff;--shiki-dark-bg:#24292e"
    // for bg. Parse these into proper CSS style objects with custom properties.
    const preStyle = useMemo(() => {
      const style: Record<string, string> = {};
      for (const raw of [tokenized.bg, tokenized.fg]) {
        for (const part of raw.split(';')) {
          const trimmed = part.trim();
          if (!trimmed) {
            continue;
          }
          if (trimmed.startsWith('--')) {
            const colonIdx = trimmed.indexOf(':');
            if (colonIdx !== -1) {
              style[trimmed.slice(0, colonIdx)] = trimmed.slice(colonIdx + 1);
            }
          } else if (raw === tokenized.bg) {
            style.backgroundColor = trimmed;
          } else {
            style.color = trimmed;
          }
        }
      }
      return style;
    }, [tokenized.bg, tokenized.fg]);

    const keyedLines = useMemo(() => addKeysToTokens(tokenized.tokens), [tokenized.tokens]);

    return (
      <pre
        className={cn(
          'm-0 box-border w-max min-w-full p-4 text-sm dark:bg-(--shiki-dark-bg)! dark:text-(--shiki-dark)!',
          className,
        )}
        style={preStyle}
      >
        <code
          className={cn(
            'font-mono text-sm',
            showLineNumbers && '[counter-increment:line_0] [counter-reset:line]',
          )}
        >
          {keyedLines.map((keyedLine, index) => (
            <LineSpan
              key={keyedLine.key}
              keyedLine={keyedLine}
              showLineNumbers={showLineNumbers}
              isHighlighted={highlightedLines?.includes(index + 1) ?? false}
              isHighlightStart={!highlightedLines?.includes(index)}
              isHighlightEnd={!highlightedLines?.includes(index + 2)}
              highlightTone={highlightTone}
            />
          ))}
        </code>
      </pre>
    );
  },
  (prevProps, nextProps) =>
    prevProps.tokenized === nextProps.tokenized &&
    prevProps.showLineNumbers === nextProps.showLineNumbers &&
    prevProps.highlightedLines === nextProps.highlightedLines &&
    prevProps.highlightTone === nextProps.highlightTone &&
    prevProps.className === nextProps.className,
);

CodeBlockBody.displayName = 'CodeBlockBody';

export const CodeBlockContainer = ({
  className,
  language,
  style,
  ...props
}: HTMLAttributes<HTMLDivElement> & { language: string }) => (
  <div
    className={cn(
      'group relative box-border min-w-0 w-full max-w-full overflow-hidden rounded-md border bg-background text-foreground',
      className,
    )}
    data-language={language}
    style={{
      containIntrinsicSize: 'auto 200px',
      contentVisibility: 'auto',
      ...style,
    }}
    {...props}
  />
);

export const CodeBlockHeader = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex items-center justify-between border-b bg-muted/80 px-3 py-2 text-muted-foreground text-xs',
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

export const CodeBlockTitle = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center gap-2', className)} {...props}>
    {children}
  </div>
);

export const CodeBlockFilename = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('font-mono', className)} {...props}>
    {children}
  </span>
);

export const CodeBlockActions = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('-my-1 -mr-1 flex items-center gap-2', className)} {...props}>
    {children}
  </div>
);

export const CodeBlockContent = ({
  code,
  language,
  showLineNumbers = false,
  highlightedLines,
  highlightTone = 'info',
}: {
  code: string;
  language: CodeBlockLanguage;
  showLineNumbers?: boolean;
  highlightedLines?: number[];
  highlightTone?: HighlightTone;
}) => {
  // Memoized raw tokens for immediate display
  const rawTokens = useMemo(() => createRawTokens(code), [code]);

  // Try to get cached result synchronously, otherwise use raw tokens
  const [tokenized, setTokenized] = useState<TokenizedCode>(
    () => highlightCode(code, language) ?? rawTokens,
  );

  useEffect(() => {
    let cancelled = false;

    // Reset to raw tokens when code changes (shows current code, not stale tokens)
    // and subscribe to the async result with a single highlight request.
    setTokenized(
      highlightCode(code, language, (result) => {
        if (!cancelled) {
          setTokenized(result);
        }
      }) ?? rawTokens,
    );

    return () => {
      cancelled = true;
    };
  }, [code, language, rawTokens]);

  return (
    <div className="relative min-w-0 max-w-full overflow-auto">
      <CodeBlockBody
        showLineNumbers={showLineNumbers}
        tokenized={tokenized}
        highlightedLines={highlightedLines}
        highlightTone={highlightTone}
      />
    </div>
  );
};

export const CodeBlock = ({
  code,
  language,
  showLineNumbers = false,
  highlightedLines,
  highlightTone = 'info',
  className,
  children,
  ...props
}: CodeBlockProps) => {
  const contextValue = useMemo(() => ({ code }), [code]);

  return (
    <CodeBlockContext value={contextValue}>
      <CodeBlockContainer className={className} language={language} {...props}>
        {children}
        <CodeBlockContent
          code={code}
          language={language}
          showLineNumbers={showLineNumbers}
          highlightedLines={highlightedLines}
          highlightTone={highlightTone}
        />
      </CodeBlockContainer>
    </CodeBlockContext>
  );
};

export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
};

export const CodeBlockCopyButton = ({
  onCopy,
  onError,
  timeout = 2000,
  children,
  className,
  ...props
}: CodeBlockCopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<number>(0);
  const { code } = use(CodeBlockContext);

  const copyToClipboard = useCallback(async () => {
    if (typeof window === 'undefined' || !navigator?.clipboard?.writeText) {
      onError?.(new Error('Clipboard API not available'));
      return;
    }

    try {
      if (!isCopied) {
        await navigator.clipboard.writeText(code);
        setIsCopied(true);
        onCopy?.();
        timeoutRef.current = window.setTimeout(() => setIsCopied(false), timeout);
      }
    } catch (error) {
      onError?.(error as Error);
    }
  }, [code, onCopy, onError, timeout, isCopied]);

  useEffect(
    () => () => {
      window.clearTimeout(timeoutRef.current);
    },
    [],
  );

  const Icon = isCopied ? CheckIcon : CopyIcon;

  return (
    <Button
      className={cn('size-7 shrink-0', className)}
      onClick={copyToClipboard}
      size="icon"
      variant="ghost"
      {...props}
    >
      {children ?? <Icon size={14} />}
    </Button>
  );
};

export type CodeBlockLanguageSelectorProps = ComponentProps<typeof Select>;

export const CodeBlockLanguageSelector = (props: CodeBlockLanguageSelectorProps) => (
  <Select {...props} />
);

export type CodeBlockLanguageSelectorTriggerProps = ComponentProps<typeof SelectTrigger>;

export const CodeBlockLanguageSelectorTrigger = ({
  className,
  ...props
}: CodeBlockLanguageSelectorTriggerProps) => (
  <SelectTrigger
    className={cn('h-7 border-none bg-transparent px-2 text-xs shadow-none', className)}
    {...props}
  />
);

export type CodeBlockLanguageSelectorValueProps = ComponentProps<typeof SelectValue>;

export const CodeBlockLanguageSelectorValue = (props: CodeBlockLanguageSelectorValueProps) => (
  <SelectValue {...props} />
);

export type CodeBlockLanguageSelectorContentProps = ComponentProps<typeof SelectContent>;

export const CodeBlockLanguageSelectorContent = ({
  align = 'end',
  ...props
}: CodeBlockLanguageSelectorContentProps) => <SelectContent align={align} {...props} />;

export type CodeBlockLanguageSelectorItemProps = ComponentProps<typeof SelectItem>;

export const CodeBlockLanguageSelectorItem = (props: CodeBlockLanguageSelectorItemProps) => (
  <SelectItem {...props} />
);
